// Pure helpers for filtering the admin-portal sidebar by the
// authenticated user's permission set. Permission strings come from
// `/auth/me`, which admin-bff sources from the role_permissions table
// seeded in 0001_seed_rbac.sql.
//
// Anything not unit-tested here is a regression risk — keep the logic
// pure (no React, no fetch) and add a test before changing behavior.

export interface NavItem {
  name: string;
  icon: string;
}

// Map each nav item to a list of permissions; the user must hold AT
// LEAST ONE to see the item. `null` means "always visible" (Command
// Center is the home tile that everyone with admin access lands on).
const NAV_PERMS: Record<string, string[] | null> = {
  "Command Center": null,
  "Tenants": ["tenant:read"],
  "Users & Teams": ["user:read"],
  // Always visible — a manager sees their reports; non-managers see an empty
  // state. No permission gate needed (data is self-scoped by /users/my-team).
  "My Team": null,
  "Billing": ["billing:read"],
  // Pillar 5 coupons — create/manage discount coupons. Gated on the
  // `billing:coupons` permission seeded in admin-bff migration
  // 0011_billing_coupons_perm.sql (OWNER/CEO/CFO).
  "Coupons": ["billing:coupons"],
  "Integrations": ["integration:read"],
  "Support Inbox": ["support:read"],
  "Live Chat": ["support:read", "support:reply"],
  "AI Agent Ops": ["ai:read"],
  "Approvals": ["approval:read", "approval:approve"],
  // Authorising a mass delete is deliberately NOT the same permission as
  // reading the business-approval queue: being able to see requests is a long
  // way from being allowed to destroy a tenant.
  "Guardrail Approvals": ["policy:approve-critical"],
  "Security": ["security:read"],
  // A4.2 Policy Simulator — gated on policy:simulate (seeded in admin-bff
  // migration 0008_seed_policy_permissions.sql). Role authoring inside the
  // section is additionally gated on policy:write at the action level.
  "Policy Simulator": ["policy:simulate"],
  "System Health": ["system:health", "system:logs", "system:errors"],
  "Logs & Errors": ["system:logs", "system:errors"],
  "Feature Flags": ["feature_flag:read"],
  "Announcements": ["announce:read"],
  "Settings": ["settings:read"],
  // Day 14 Slice F — role-scoped dashboards. Each entry gates on the
  // matching `dashboard:*` permission seeded in admin-bff migration
  // 0006_slice_f_role_dashboards.sql.
  "Legal": ["dashboard:legal"],
  "Tech Support": ["dashboard:tech_support"],
  "Live Agent": ["dashboard:live_agent"],
  "Billing Ops": ["dashboard:billing"],
  "Technical": ["dashboard:technical"],
  // Pillar 4 — marketplace app-review desk. Gated on marketplace:review
  // (OWNER/CEO/CTO/ENGINEER), seeded in admin-bff migration 0013.
  "App Review": ["marketplace:review"],
  // Pillar 6A — Employee OS. Gated on employee:read (OWNER/CEO/CTO/CFO/PEOPLE_OPS),
  // seeded in admin-bff migration 0014.
  "People": ["employee:read"],
};

/** Visible if the item has no required perms, or the user holds at least one. */
export function isNavItemVisible(itemName: string, userPermissions: ReadonlySet<string>): boolean {
  const required = NAV_PERMS[itemName];
  if (required === undefined) {
    // Unknown nav items default to visible — better to surface a new
    // section to admins than to hide it silently if someone forgot to
    // register a permission mapping.
    return true;
  }
  if (required === null) return true;
  return required.some((perm) => userPermissions.has(perm));
}

export function filterNav(nav: ReadonlyArray<NavItem>, userPermissions: ReadonlyArray<string>): NavItem[] {
  const set = new Set(userPermissions);
  return nav.filter((item) => isNavItemVisible(item.name, set));
}

/**
 * Pick the active section for a freshly-rendered sidebar. If the
 * previously-active section is no longer visible (user lost access,
 * or signed in with a more restricted role), fall back to the first
 * visible item — Command Center will always pass since it has no perm
 * gate.
 */
export function resolveActiveSection(
  current: string,
  visible: ReadonlyArray<NavItem>,
): string {
  if (visible.some((i) => i.name === current)) return current;
  return visible[0]?.name ?? "Command Center";
}
